/**
 * Phase 6a — react-query fetchers for orbit style-stage + jobs list.
 */

import { apiFetch } from "@/lib/apiFetch";
import { DEFAULT_STUDIO_ORG_KEY } from "@/lib/shoeStudioPrefs";
import type { OrbitListJob } from "./orbitUi";
import { isOrbitRunningStatus, parseOrbitFixtureFromSearch } from "./orbitUi";

export type OrbitStyleStageResponse = {
  ok: true;
  stage: "orbit";
  style_id: string;
  job: OrbitListJob | null;
};

/** True when the page URL requests a fixture orbit (?orbitFixture=…). */
export function shouldIncludeOrbitFixture(
  search: string =
    typeof window !== "undefined" ? window.location.search : "",
): boolean {
  return parseOrbitFixtureFromSearch(search) != null;
}

export async function fetchOrbitStyleStage(
  styleId: string,
  orgKey: string = DEFAULT_STUDIO_ORG_KEY,
): Promise<OrbitListJob | null> {
  const params = new URLSearchParams({
    style_id: styleId,
    stage: "orbit",
    org_key: orgKey,
  });
  // F-0: include_fixture only when URL has ?orbitFixture= (server still
  // requires STRONOM_ORBIT_FIXTURE_USER_IDS).
  if (shouldIncludeOrbitFixture()) {
    params.set("include_fixture", "1");
  }
  const res = await apiFetch(`/api/style-stage?${params.toString()}`);
  const data = (await res.json()) as OrbitStyleStageResponse & {
    ok?: boolean;
    error?: string;
  };
  if (!data?.ok) return null;
  return data.job ?? null;
}

export type OrbitJobsResponse = {
  ok: true;
  jobs: OrbitListJob[];
};

export async function fetchOrbitJobs(
  orgKey: string = DEFAULT_STUDIO_ORG_KEY,
): Promise<OrbitListJob[]> {
  const params = new URLSearchParams({ org_key: orgKey });
  const res = await apiFetch(`/api/orbit/jobs?${params.toString()}`);
  const data = (await res.json()) as OrbitJobsResponse & {
    ok?: boolean;
    jobs?: OrbitListJob[];
  };
  if (!Array.isArray(data?.jobs)) return [];
  return data.jobs;
}

export function orbitStyleQueryKey(styleId: string | null | undefined) {
  return ["orbit", styleId ?? ""] as const;
}

export function orbitJobsQueryKey(orgKey: string = DEFAULT_STUDIO_ORG_KEY) {
  return ["orbit", "jobs", orgKey] as const;
}

export function orbitRefetchIntervalMs(
  job: OrbitListJob | null | undefined,
): number | false {
  if (job && isOrbitRunningStatus(job.status)) return 15_000;
  return false;
}

/**
 * Optimistic running job after generate 201 / 409 ORBIT_IN_PROGRESS.
 * G-2: write this into the style query cache immediately so the popover
 * shows "Preparing…" and the chip loader appears before the first refetch.
 */
export function orbitOptimisticRunningJob(args: {
  jobId: string;
  styleId: string | null | undefined;
  status?: string | null;
  stage?: string | null;
  createdAt?: string;
  isFixture?: boolean;
}): OrbitListJob {
  const now = args.createdAt ?? new Date().toISOString();
  return {
    id: args.jobId,
    style_id: args.styleId ?? null,
    parent_job_id: null,
    status: args.status && isOrbitRunningStatus(args.status) ? args.status : "queued",
    stage: args.stage ?? "submit",
    attempt: 1,
    attempt_started_at: now,
    result: null,
    error: null,
    viewed: false,
    is_fixture: args.isFixture === true,
    created_at: now,
    updated_at: now,
  };
}

/** Parse generate 201 / 409 body into an optimistic OrbitListJob, or null. */
export function orbitJobFromGenerateResponse(args: {
  status: number;
  data: {
    job?: {
      id?: string;
      style_id?: string | null;
      status?: string;
      stage?: string | null;
    } | null;
    jobId?: string | null;
    code?: string | null;
  };
  styleId: string | null | undefined;
  isFixture?: boolean;
  createdAt?: string;
}): OrbitListJob | null {
  const { status, data, styleId } = args;
  if (status === 201 && data?.job?.id) {
    return orbitOptimisticRunningJob({
      jobId: data.job.id,
      styleId: data.job.style_id ?? styleId,
      status: "queued",
      stage: "submit",
      createdAt: args.createdAt,
      isFixture: args.isFixture,
    });
  }
  if (status === 409 && data?.code === "ORBIT_IN_PROGRESS") {
    const jobId = data.jobId ?? data.job?.id;
    if (!jobId) {
      // Still seed a synthetic running job so UI leaves Generate immediately.
      return orbitOptimisticRunningJob({
        jobId: `pending-${styleId ?? "unknown"}`,
        styleId,
        status: "queued",
        stage: "submit",
        createdAt: args.createdAt,
        isFixture: args.isFixture,
      });
    }
    return orbitOptimisticRunningJob({
      jobId,
      styleId: data.job?.style_id ?? styleId,
      status: "queued",
      stage: "submit",
      createdAt: args.createdAt,
      isFixture: args.isFixture,
    });
  }
  return null;
}

/**
 * G-2: setQueryData to running job, then invalidate so style-stage refetch
 * (and refetchInterval 15s while queued/processing/waiting) takes over.
 */
export function applyOrbitGenerateSuccess(args: {
  queryClient: {
    setQueryData: (key: readonly unknown[], data: OrbitListJob) => void;
    invalidateQueries: (opts: { queryKey: readonly unknown[] }) => unknown;
  };
  styleId: string;
  status: number;
  data: {
    job?: {
      id?: string;
      style_id?: string | null;
      status?: string;
      stage?: string | null;
    } | null;
    jobId?: string | null;
    code?: string | null;
  };
  isFixture?: boolean;
  createdAt?: string;
}): OrbitListJob | null {
  const job = orbitJobFromGenerateResponse({
    status: args.status,
    data: args.data,
    styleId: args.styleId,
    isFixture: args.isFixture,
    createdAt: args.createdAt,
  });
  if (!job) return null;
  const key = orbitStyleQueryKey(args.styleId);
  args.queryClient.setQueryData(key, job);
  void args.queryClient.invalidateQueries({ queryKey: key });
  return job;
}

export type OrbitFramesQuality = "auto" | "720" | "hd";

export type OrbitFramesResponse = {
  quality: "720" | "hd";
  width: number | null;
  height: number | null;
  frames: string[];
  markers: Record<string, number | null>;
  open_index: number | null;
  result: unknown;
  video_url: string | null;
  video_hd_url: string | null;
  expires_in: number;
};

export async function fetchOrbitFrames(
  jobId: string,
  quality: OrbitFramesQuality = "auto",
  opts?: { stride?: number },
): Promise<OrbitFramesResponse> {
  const params = new URLSearchParams({ jobId, quality });
  if (opts?.stride != null && opts.stride > 1) {
    params.set("stride", String(opts.stride));
  }
  const res = await apiFetch(`/api/orbit/frames?${params.toString()}`);
  const data = (await res.json()) as OrbitFramesResponse & { error?: string };
  if (!res.ok) {
    throw new Error(
      typeof data?.error === "string" ? data.error : "Failed to load orbit frames",
    );
  }
  if (!Array.isArray(data.frames)) {
    throw new Error("Invalid frames response");
  }
  return data as OrbitFramesResponse;
}

/** Popover READY preview: 720p + stride 4. */
export const ORBIT_PREVIEW_STRIDE = 4;
export const ORBIT_PREVIEW_STALE_MS = 50 * 60_000;

export function orbitFramesPreviewQueryKey(jobId: string | null | undefined) {
  return ["orbit", "frames", jobId ?? "", "720", `stride-${ORBIT_PREVIEW_STRIDE}`] as const;
}

export async function fetchOrbitFramesPreview(
  jobId: string,
): Promise<OrbitFramesResponse> {
  return fetchOrbitFrames(jobId, "720", { stride: ORBIT_PREVIEW_STRIDE });
}

/** Orbit view-orientation gate (+ 5 thumbs). */
export type OrbitViewCheckView = {
  kind: string;
  label: string;
  thumb_url: string | null;
  expected: "left" | "right" | null;
  toe_points: string | null;
  ok: boolean | null;
};

export type OrbitViewCheckResponse = {
  view_set_key: string;
  legacy: boolean;
  cached: boolean;
  checked_at: string;
  views: OrbitViewCheckView[];
};

export async function fetchOrbitViewCheck(
  anglesJobId: string,
  opts?: { refresh?: boolean },
): Promise<OrbitViewCheckResponse> {
  const res = await apiFetch("/api/orbit/view-check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      anglesJobId,
      ...(opts?.refresh ? { refresh: true } : {}),
    }),
  });
  const data = (await res.json()) as OrbitViewCheckResponse & {
    error?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data?.error === "string" ? data.error : "View check failed",
    );
  }
  if (!Array.isArray(data.views)) {
    throw new Error("Invalid view-check response");
  }
  return data;
}

export function orbitViewCheckQueryKey(anglesJobId: string | null | undefined) {
  return ["orbit", "view-check", anglesJobId] as const;
}

export function orbitFramesQueryKey(
  jobId: string | null | undefined,
  quality: OrbitFramesQuality,
) {
  return ["orbit", "frames", jobId ?? "", quality] as const;
}
